<script setup lang="ts">
  import FlowPathInput from '@/components/form/FlowPathInput.vue'
  import {
    DropDown,
    FormFieldContainer,
    iconMap,
    InputField,
    Reorder,
    SwitchButton,
    UiButton,
    type ButtonGridElement,
  } from '@logistiek-zonder-papier/lzp-library'
  import ColorPicker from '@/components/form-wizard/ColorPicker.vue'
  import { reactive } from 'vue'

  const field = defineModel<ButtonGridElement>({ required: true })

  const icons = Object.keys(iconMap).map(name => ({ name }))

  const touched = reactive({
    storeLocation: false,
  })

  function validateRequired(key: 'storeLocation', message: string) {
    return touched[key] && field.value?.type === 'buttonGrid' && !field.value?.[key]?.trim()
      ? message
      : null
  }

  function addButton() {
    field.value.buttons.push({
      label: '',
      value: '',
      icon: '',
      buttonColor: '',
      textColor: '',
      iconColor: '',
    })
  }

  function deleteButton(index: number) {
    field.value.buttons.splice(index, 1)
  }
</script>

<template>
  <div class="field-config-container">
    <FormFieldContainer label="Label">
      <FlowPathInput
        placeholder="e.g. ${customer.name}"
        hint="Field label. Supports ${...} interpolation."
        v-model="field.label"
        :optional="true"
        type="text"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer
      label="Store Location"
      :error="validateRequired('storeLocation', 'Store Location is required')"
    >
      <InputField
        placeholder="x.path.to.location"
        hint="Where the value of the pressed button is stored."
        v-model="field.storeLocation"
        type="text"
        @blur="touched.storeLocation = true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Buttons">
      <template #action-button>
        <UiButton
          label="Add button"
          variant="prominent"
          size="small"
          icon="plus"
          @click="addButton"
        />
      </template>
      <div class="repeatable">
        <div v-for="(button, index) in field.buttons" :key="index" class="section">
          <FormFieldContainer :label="`Button ${index + 1}`">
            <template #action-button>
              <Reorder v-model="field.buttons" :index="index" />
              <UiButton
                icon="delete"
                size="small"
                variant="negative"
                @click="deleteButton(index)"
              />
            </template>
            <FlowPathInput
              label="Label"
              placeholder="Literal label or ${xpathvalue}"
              v-model="button.label"
              :optional="false"
              type="text"
              :validate-paths="true"
            />
            <FormFieldContainer label="Value">
              <InputField
                placeholder="Value written to the store location"
                v-model="button.value"
                type="text"
              />
            </FormFieldContainer>
            <FormFieldContainer label="Icon">
              <DropDown
                idKey="name"
                :modelValue="button.icon ? { name: button.icon } : null"
                @update:modelValue="
                  (value: { name: string } | null | undefined) => (button.icon = value?.name ?? '')
                "
                :data="icons"
                enableSearch
                required
              >
                <template #empty>Select an icon</template>
                <template #item="{ item }">
                  <span v-if="item" class="icon-option">
                    <component :is="iconMap[item.name]" class="icon" />
                    {{ item.name }}
                  </span>
                </template>
              </DropDown>
              <FormFieldContainer label="Text Colour" class="color-field">
                <div class="inline">
                  <ColorPicker v-model="button.textColor" />
                  <InputField type="text" v-model="button.textColor" />
                </div>
              </FormFieldContainer>
              <FormFieldContainer label="Icon Colour" class="color-field">
                <div class="inline">
                  <ColorPicker v-model="button.iconColor" />
                  <InputField type="text" v-model="button.iconColor" />
                </div>
              </FormFieldContainer>
              <FormFieldContainer label="Button Colour" class="color-field">
                <div class="inline">
                  <ColorPicker v-model="button.buttonColor" />
                  <InputField type="text" v-model="button.buttonColor" />
                </div>
              </FormFieldContainer>
            </FormFieldContainer>
          </FormFieldContainer>
        </div>
      </div>
    </FormFieldContainer>

    <FormFieldContainer label="Required">
      <SwitchButton v-model="field.required" />
    </FormFieldContainer>

    <FormFieldContainer label="Error message">
      <FlowPathInput
        placeholder="Error message under the field"
        v-model="field.error"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>
  </div>
</template>

<style scoped>
  .field-config-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .repeatable {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .section {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-xs);
    border-radius: var(--components-form-field-corners);
    border: 1px solid var(--colours-grey-3);
    box-shadow: 1px 1px 1px 1px var(--colours-grey-3);
  }

  .icon-option {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
  }

  .icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .inline {
    width: 100%;
    display: flex;
    gap: var(--spacing-s);
    align-items: center;
    justify-content: space-between;
  }
</style>
